import "./login.css";
import { Globe } from "lucide-react";
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslate } from "@/lib/i18n";
import type { ar } from "@/lib/i18n/messages";
import {
  ILLUSTRATION_CSS,
  LOGIN_ILLUSTRATIONS,
  type LoginIllustrationId,
} from "./loginIllustrations";
import { rememberedStore } from "./rememberedStore";

type Messages = typeof ar;

type Slide = {
  art: LoginIllustrationId;
  /** The panel's fill, and (deepened) the blob behind the page. */
  tint: string;
  title: (m: Messages) => string;
  body: (m: Messages) => string;
};

/** What the product does, one sentence each, while staff wait to log in. */
const SLIDES: readonly Slide[] = [
  { art: "welcome", tint: "oklch(0.96 0.03 165)", title: (m) => m.auth.slideFloorTitle, body: (m) => m.auth.slideFloorBody },
  { art: "chef", tint: "oklch(0.97 0.035 85)", title: (m) => m.auth.slideKitchenTitle, body: (m) => m.auth.slideKitchenBody },
  { art: "rider", tint: "oklch(0.96 0.025 240)", title: (m) => m.auth.slideDeliveryTitle, body: (m) => m.auth.slideDeliveryBody },
  { art: "cashier", tint: "oklch(0.96 0.03 300)", title: (m) => m.auth.slideCashTitle, body: (m) => m.auth.slideCashBody },
];

const SLIDE_MS = 4500;

/** The product's name, never translated. */
const TRIOTY = "Trioty";

/** The blob is the slide's tint, a step deeper so it reads on white. */
const blobTint = (tint: string) => tint.replace(/oklch\(0\.9[67]/, "oklch(0.93");

let illustrationCssInjected = false;
function ensureIllustrationCss() {
  if (illustrationCssInjected || typeof document === "undefined") return;
  illustrationCssInjected = true;
  const style = document.createElement("style");
  style.dataset.loginIllustrations = "";
  style.textContent = ILLUSTRATION_CSS;
  document.head.appendChild(style);
}

/** One of the four drawings, at `size` px. Static markup — see the data file. */
export function LoginIllustration({ id, size }: { id: LoginIllustrationId; size: number }) {
  useEffect(ensureIllustrationCss, []);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 160 160"
      fill="none"
      stroke="#1d1d1f"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ display: "block", flex: "none", overflow: "visible" }}
      dangerouslySetInnerHTML={{ __html: LOGIN_ILLUSTRATIONS[id] }}
    />
  );
}

/** Switches `/:locale/…` to the other language, keeping the rest of the URL. */
function LanguageButton({ className }: { className?: string }) {
  const { locale = "ar" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const next = locale === "ar" ? "en" : "ar";
  return (
    <button
      type="button"
      className={["auth-lang", className].filter(Boolean).join(" ")}
      data-testid="auth-language"
      onClick={() =>
        navigate(
          `${location.pathname.replace(/^\/(ar|en)(?=\/|$)/, `/${next}`)}${location.search}`,
          { replace: true },
        )
      }
    >
      <Globe size={14} strokeWidth={1.8} aria-hidden />
      <span>{next === "ar" ? "العربية" : "English"}</span>
    </button>
  );
}

/**
 * THE PAGE AROUND THE LOGIN AND THE FIRST-PASSWORD FORMS.
 *
 * The form column on the start side; from 900px a carousel of what the product
 * does on the end side, over a soft blob tinted by the slide on show. Below
 * 900px the carousel goes and the blob moves behind the heading, sized to end
 * just under the subtitle (`subtitleRef`).
 *
 * The brand in the corner is the restaurant this device last signed into, if
 * it remembers one — the shared till's own name — and Trioty's mark otherwise.
 */
// eslint-disable-next-line no-restricted-syntax -- the signed-out login page is no portal frame: it has no nav, sidebar or header to share with @a2z/ui, only this screen's own two columns.
export function AuthShell({
  subtitleRef,
  children,
}: {
  /** The subtitle under the heading: the narrow blob ends just below it. */
  subtitleRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const t = useTranslate();
  const { locale = "ar" } = useParams();
  const [slide, setSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [blobHeight, setBlobHeight] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const store = rememberedStore();

  useEffect(ensureIllustrationCss, []);

  useEffect(() => {
    if (!autoplay) return;
    const id = window.setInterval(
      () => setSlide((current) => (current + 1) % SLIDES.length),
      SLIDE_MS,
    );
    return () => window.clearInterval(id);
  }, [autoplay]);

  useLayoutEffect(() => {
    const measure = () => {
      const sub = subtitleRef.current;
      const root = rootRef.current;
      if (!sub || !root) return;
      const y = sub.getBoundingClientRect().bottom - root.getBoundingClientRect().top;
      // 80px above the page (the blob starts at -80px) and 28px of air under
      // the subtitle.
      setBlobHeight(Math.round(y + 80 + 28));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });

  const current = SLIDES[slide];

  return (
    <div
      ref={rootRef}
      className="auth-root"
      dir={locale === "ar" ? "rtl" : "ltr"}
      style={blobHeight ? ({ "--auth-blob-h": `${blobHeight}px` } as CSSProperties) : undefined}
    >
      <div className="auth-blob" aria-hidden style={{ background: blobTint(current.tint) }} />

      <div className="auth-column">
        <div className="auth-head">
          <div className="auth-brand" data-testid="auth-brand">
            {store ? (
              <>
                {store.logoUrl ? (
                  <img src={store.logoUrl} alt="" className="auth-brand-logo" />
                ) : (
                  <span className="auth-brand-mark">{Array.from(store.name)[0]?.toUpperCase()}</span>
                )}
                <span>{store.name.toUpperCase()}</span>
              </>
            ) : (
              // No restaurant known yet: Trioty's full wordmark, mark and name.
              <img src="/logo-sidebar.svg" alt={TRIOTY} className="auth-brand-wordmark" />
            )}
          </div>
          <LanguageButton className="auth-narrow-only" />
        </div>
        <div className="auth-main">
          <div className="auth-form-col">{children}</div>
        </div>
        {store ? <div className="auth-powered">{t((m) => m.auth.poweredBy)}</div> : null}
      </div>

      <div className="auth-panel auth-wide-only" data-testid="auth-carousel">
        <div className="auth-panel-lang">
          <LanguageButton />
        </div>
        <div className="auth-slide-art">
          <div key={current.art}>
            <LoginIllustration id={current.art} size={340} />
          </div>
        </div>
        <div className="auth-slide-text" aria-live="polite">
          <span className="auth-slide-title">{t(current.title)}</span>
          <span className="auth-slide-body">{t(current.body)}</span>
        </div>
        <div className="auth-dots">
          {SLIDES.map((entry, index) => (
            <button
              key={entry.art}
              type="button"
              className="auth-dot"
              aria-label={t(entry.title)}
              aria-current={index === slide ? "true" : undefined}
              onClick={() => {
                setAutoplay(false);
                setSlide(index);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
